/**
 * Ex-Demo collection compact filters — model tags from catalog:model:* names.
 * Also hides Ex-Demo on regular category pages unless opted in.
 */
(function (root) {
  'use strict';

  const ON_CATEGORY_TAGS = new Set(['ex-demo-on-category', 'ex_demo_on_category']);

  function tagList(tags) {
    return (Array.isArray(tags) ? tags : String(tags || '').split(',')).map((t) =>
      String(t).trim()
    );
  }

  function modelsFromProductTags(tags) {
    const out = [];
    for (const raw of tagList(tags)) {
      const match = String(raw).trim().match(/^catalog:model:(.+)$/i);
      if (!match) continue;
      const value = match[1].trim();
      if (!value || /^\d+$/.test(value)) continue;
      if (/^ex-demo/i.test(value)) continue;
      out.push(value);
    }
    return out;
  }

  function isExDemoProduct(product) {
    if (!product) return false;
    const title = String(product.title || '');
    if (/ex-demo/i.test(title)) return true;
    const tags = tagList(product.tags);
    if (tags.some((t) => t.toLowerCase() === 'ex-demo')) return true;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const skus = [product.variants?.[0]?.sku, product.sku, ...variants.map((v) => v && v.sku)];
    return skus.some((raw) => {
      const sku = String(raw || '')
        .trim()
        .toUpperCase();
      return sku.startsWith('EX-') || sku.startsWith('EA-') || sku.endsWith('-EX');
    });
  }

  function isExDemoAllowedOnCategory(product, allowHandles) {
    if (!product) return false;
    const tags = tagList(product.tags).map((t) => t.toLowerCase());
    if (tags.some((t) => ON_CATEGORY_TAGS.has(t))) return true;
    const handle = String(product.handle || '').toLowerCase();
    const allow = Array.isArray(allowHandles) ? allowHandles : [];
    return handle !== '' && allow.includes(handle);
  }

  function applyCategoryExDemoRules(products, options) {
    const opts = options || {};
    if (opts.exDemoCollection) return Array.isArray(products) ? products.slice() : [];
    const allow = (opts.allowHandles || [])
      .map((h) => String(h || '').trim().toLowerCase())
      .filter(Boolean);
    const list = Array.isArray(products) ? products : [];
    const next = [];
    const exdemo = [];
    for (const product of list) {
      if (!isExDemoProduct(product)) {
        next.push(product);
        continue;
      }
      if (isExDemoAllowedOnCategory(product, allow)) exdemo.push(product);
    }
    return next.concat(exdemo);
  }

  const LISTING_TITLE_TOKENS = [
    'Street Photography Bundle Plus',
    'Vlog Bundle Plus',
    'Racing Bundle Plus',
    'Hiking Bundle Plus',
    'Diving Bundle Plus',
    'Cycling Bundle Plus',
    'Scuba Bundle Plus',
    'Street Photography Bundle',
    'Vlog Bundle',
    'Racing Bundle',
    'Hiking Bundle',
    'Diving Bundle',
    'Cycling Bundle',
    'Scuba Bundle',
    'Fly More Combo',
    'Standard Combo',
    'Creator Combo',
    'Early Bird Vlog Bundle',
    'Early Bird Bundle',
    'Ultimate Accessory Combo',
    'Ultimate Vlog Combo',
    'Ultimate Combo',
    'Platinum Combo',
    'Platinum Bundle',
    'Basic Package',
    'Bundle Plus',
  ];

  function splitListingTitle(title) {
    const raw = String(title || '').trim();
    for (const token of LISTING_TITLE_TOKENS) {
      const idx = raw.indexOf(token);
      if (idx <= 0) continue;
      const productTitle = raw.slice(0, idx).trim();
      if (!productTitle) continue;
      return { productTitle, variantTitle: raw.slice(idx).trim() };
    }
    return { productTitle: raw, variantTitle: '' };
  }

  function stackBundleCardTitle(fullTitle, productName) {
    const full = String(fullTitle || '').trim();
    const product = String(productName || '').trim();
    if (!full) return { bundleTitle: '', productTitle: '' };
    if (product && full === product) return { bundleTitle: full, productTitle: '' };
    const prefix = product ? `${product} ` : '';
    if (prefix && full.startsWith(prefix)) {
      const bundle = full.slice(prefix.length).trim();
      if (bundle) return { bundleTitle: bundle, productTitle: product };
    }
    if (product && full !== product) return { bundleTitle: full, productTitle: product };
    const split = splitListingTitle(full);
    if (split.variantTitle) {
      return { bundleTitle: split.variantTitle, productTitle: split.productTitle };
    }
    return { bundleTitle: full, productTitle: '' };
  }

  function decorateListingTitles(products) {
    const list = Array.isArray(products) ? products : [];
    return list.map((product) => {
      if (!product) return product;
      if (product.variantTitle) return product;
      const variant = Array.isArray(product.variants) ? product.variants[0] : null;
      const fromVariant = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
      if (fromVariant) return { ...product, variantTitle: fromVariant };
      const split = splitListingTitle(product.title);
      if (!split.variantTitle) return product;
      return { ...product, title: split.productTitle, variantTitle: split.variantTitle };
    });
  }

  function imageKey(img) {
    if (!img) return '';
    if (typeof img === 'string') return img;
    return String(img.id || img.src || '');
  }

  function hasVariantId(ids, variantId) {
    const id = Number(variantId);
    if (!Number.isFinite(id)) return false;
    return (Array.isArray(ids) ? ids : []).some((raw) => Number(raw) === id);
  }

  function pickVariantCardImages(product, variant) {
    const all = Array.isArray(product && product.images) ? product.images : [];
    const max = 5;
    if (!variant) return all.slice(0, max);

    const out = [];
    const seen = new Set();
    const push = (img) => {
      if (!img || out.length >= max) return;
      const key = imageKey(img);
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      out.push(img);
    };

    if (variant.featured_image) push(variant.featured_image);
    for (const img of all) {
      if (out.length >= max) break;
      if (!hasVariantId(img && img.variant_ids, variant.id)) continue;
      push(img);
    }
    if (!out.length && product && product.featured_image) push(product.featured_image);
    if (!out.length && all[0]) push(all[0]);
    return out;
  }

  function expandPricedVariants(products) {
    const out = [];
    const list = Array.isArray(products) ? products : [];
    for (const product of list) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      for (const variant of variants) {
        const price = parseFloat(variant && variant.price);
        if (!Number.isFinite(price) || price <= 0) continue;
        const title = variant.title && variant.title !== 'Default Title' ? variant.title : '';
        const handle = String(product.handle || '').trim();
        out.push({
          ...product,
          variants: [variant],
          available: !!variant.available,
          variantTitle: title,
          featured_image: (variant && variant.featured_image) || product.featured_image,
          images: pickVariantCardImages(product, variant),
          url: handle
            ? `/products/${handle}?variant=${variant.id}`
            : product.url,
        });
      }
    }
    return out;
  }

  function compactCardCta(product, options) {
    const opts = options || {};
    if (!opts.exDemoCollection) return { kind: 'view-more' };
    const variant = Array.isArray(product && product.variants) ? product.variants[0] : null;
    const variantId = variant && variant.id;
    const available = variant ? !!variant.available : !!(product && product.available);
    if (available && variantId) {
      return { kind: 'add-to-cart', variantId, productId: product && product.id };
    }
    return { kind: 'sold-out' };
  }

  root.D1ExDemoFilters = {
    modelsFromProductTags,
    splitListingTitle,
    stackBundleCardTitle,
    decorateListingTitles,
    expandPricedVariants,
    pickVariantCardImages,
    compactCardCta,
    isExDemoProduct,
    isExDemoAllowedOnCategory,
    applyCategoryExDemoRules,
  };
})(typeof window !== 'undefined' ? window : globalThis);
